import { Briefcase, ChartNoAxesCombined, Crosshair, FileSearch, Gauge, Layers, Radar, Zap } from '@bloo-team/ui/icons'
import { ANALYST_OR_ENGINEER, INCLUDING_VIEWER } from '@bloo-team/core-shell'
import type { ConsoleProductManifest } from '@bloo-team/core-shell'
import { vantageMaturitySignals } from './journey/signals'
import { caseSettingsGate } from './lib/case-settings-access'
import { ActivityFeed, CasesCard, CoverageRing, MitreMini, SignalsCard, TriageTables } from './home/widgets'

/**
 * Vantage — the product manifest (docs/product-manifest.md).
 *
 * Funnel-shaped: coverage through response (IA10). Routes stay in the app's
 * tree until the extraction epic. Home widgets: `./home/widgets.tsx` —
 * `order` ranks interleave with the other products' instruments
 * (docs/product-manifest.md).
 */
export const vantageManifest: ConsoleProductManifest = {
  id: 'vantage',
  name: 'Vantage',
  icon: Crosshair,
  nav: [
    { key: 'vantage-overview', title: 'Overview', icon: Gauge, to: '/vantage/overview', roles: INCLUDING_VIEWER },
    { key: 'vantage-coverage', title: 'Coverage', icon: Crosshair, to: '/vantage/coverage', roles: ANALYST_OR_ENGINEER },
    { key: 'vantage-detections', title: 'Detections', icon: Layers, to: '/vantage/detections', roles: ANALYST_OR_ENGINEER },
    { key: 'vantage-signals', title: 'Signals', icon: ChartNoAxesCombined, to: '/vantage/signals', roles: ANALYST_OR_ENGINEER },
    { key: 'vantage-cases', title: 'Cases', icon: Briefcase, to: '/vantage/cases', roles: ANALYST_OR_ENGINEER },
    { key: 'vantage-hunts', title: 'Hunts', icon: FileSearch, to: '/vantage/hunts', roles: ANALYST_OR_ENGINEER },
    { key: 'vantage-response', title: 'Response', icon: Zap, to: '/vantage/response', roles: ANALYST_OR_ENGINEER },
  ],
  settings: [
    {
      // Sections keyed 'products' merge into the one "Products" group of the
      // settings nav (docs/product-manifest.md); Vantage registers before
      // SynthAI, so its row sits above SynthAI's.
      key: 'products',
      title: 'Products',
      items: [
        {
          key: 'settings-vantage',
          title: 'Vantage',
          icon: Crosshair,
          to: '/settings/vantage',
          // The case-management service answers its settings to admins only:
          // Security Engineers (statuses, ITSM, grouping) and every Root user,
          // 'No Access' included (evidence, features) — CaseSettingsPage.
          gate: caseSettingsGate,
          admitsNoAccess: true,
          children: [{ key: 'settings-vantage-cases', title: 'Cases', icon: Briefcase, to: '/settings/vantage/cases', gate: caseSettingsGate, admitsNoAccess: true }],
        },
      ],
    },
  ],
  palette: [
    {
      id: 'start-hunt',
      title: 'Start a hunt',
      icon: FileSearch,
      keywords: 'hunt new create hypothesis threat hunting',
      to: '/vantage/hunts',
    },
    {
      id: 'triage-signals',
      title: 'Triage signals',
      icon: Radar,
      keywords: 'signal triage queue alerts storyline connected graph',
      to: '/vantage/signals',
    },
  ],
  home: [
    { key: 'vantage-home-coverage', title: 'Coverage', order: 30, render: CoverageRing },
    { key: 'vantage-home-feed', title: 'Activity feed', order: 40, render: ActivityFeed },
    { key: 'vantage-home-cases', title: 'Open cases', order: 50, render: CasesCard },
    { key: 'vantage-home-signals', title: 'Signals · 24h', order: 60, render: SignalsCard },
    { key: 'vantage-home-topn', title: 'Top-N triage', order: 90, render: TriageTables },
    { key: 'vantage-home-mitre', title: 'Tactic activity', order: 120, render: MitreMini },
  ],
  // Home · Journey: the Detect phase and the security half of Operate (S4,
  // docs/home-journey-requirements.md §5). Cheap reads only — the Detections
  // page's own counts join attack/analytics, which Home rejects.
  maturity: vantageMaturitySignals,
  demo: {
    // Vantage's demo data is deterministic and module-local (rule 6:
    // lib/signals-demo.ts rows (storylines come from the connected-signals port over them), the platform search fixtures, the
    // in-memory suppression/workbook stores). What persists between demo
    // sessions is the analyst's own drafts in localStorage — hunt drafts,
    // custom hunt templates, signal triage, signal settings. Seeding returns
    // demo mode to its fixture state by dropping those keys; idempotent, as
    // the contract requires. Nothing calls it at runtime yet — a profile
    // boot (E8) can.
    seed: () => {
      const ls = globalThis.localStorage
      if (!ls) return
      const prefixes = ['bloo.hunt.', 'bloo.hunt-tpl.', 'bloo.signal-triage.', 'bloo.signal-settings.']
      for (let i = ls.length - 1; i >= 0; i--) {
        const key = ls.key(i)
        if (key && prefixes.some((p) => key.startsWith(p))) ls.removeItem(key)
      }
    },
  },
  backend: ['vantage'],
}
